import sodium from "libsodium-wrappers-sumo";
import { quick_hash } from "#root/lib/crypto/hash";
import { autodecode, autoencode } from "#root/lib/format";

/**
 * Pluggable storage implementations for credentials.
 */
export interface Storage<T> {
  set(_in: T): void;
  get(): T;
  reset(): void;
}

/**
 * Implements a very simple in-memory storage for credentials, which is useful for tests.
 */
export class XXX_TESTING_ONLY_MemoryStorage<T> implements Storage<T> {
  private _inner: T = null;

  get(): T {
    return this._inner;
  }

  set(input: T) {
    this._inner = input;
  }

  reset() {
    this.set(null);
  }
}

/**
 * Implements a storage for credential parts that can work across multiple tabs, and across reloads.
 */
export class SyncedBrowserStorage<T> implements Storage<T> {
  private _inner: T;
  private _id: string;
  private _channel: BroadcastChannel;
  ready: Promise<void>;

  // TODO: break these out into a SimpleCodec?
  private _encode(input: T): string {
    return JSON.stringify(autoencode(input));
  }

  private _decode(input: string): T {
    return autodecode(JSON.parse(input)) as T;
  }

  constructor(
    id: string,
    private _initial: T,
  ) {
    this.ready = new Promise((resolve) => {
      sodium.ready.then(() => {
        // This is very simple obfuscation but it does help hide exactly what values are if someone looks in storage.
        this._id = sodium.to_hex(quick_hash(id, 16));
        const cachedValue = sessionStorage.getItem(this._id);
        if (cachedValue) {
          this._inner = this._decode(cachedValue);
        } else {
          this._inner = this._initial;
        }

        resolve();

        // Setup the broadcast channel for this value.
        this._channel = new BroadcastChannel(`vault-sync-${this._id}`);
        this._channel.onmessage = (event) => {
          switch (event.data[0]) {
            case "set":
              this.set(event.data[1], false);
              break;
            case "remove":
              this.set(this._initial, false);
              break;
            // TODO: add "sync" message here to bootstrap another tab
          }
        };
      });
    });
  }

  get(): T {
    return this._inner;
  }

  set(input: T, propagate = true) {
    this._inner = input;
    if (input) {
      sessionStorage.setItem(this._id, this._encode(input));
      if (propagate) {
        this._channel.postMessage(["set", input]);
      }
    } else {
      sessionStorage.removeItem(this._id);
      if (propagate) {
        this._channel.postMessage(["remove"]);
      }
    }
  }

  reset() {
    this.set(this._initial);
  }
}
